import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebase/firebaseConfig";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import dayjs from "dayjs";
import AttemptsDrawer from "./AttemptsDrawer";
import routePaths from "../../../../routes/routePaths";
import { FiAlertTriangle, FiExternalLink, FiRotateCcw, FiTrendingUp, FiUsers } from "react-icons/fi";

function studentName(stu) {
  return stu.fullName || stu.name || `${stu.firstName || ""} ${stu.lastName || ""}`.trim() || stu.email || stu.id;
}

function getPercent(item) {
  if (typeof item?.percent === "number") return Math.round(item.percent);
  if (item?.score?.total > 0) return Math.round((item.score.correct / item.score.total) * 100);
  return null;
}

function tsSeconds(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts > 1e12 ? Math.floor(ts / 1000) : ts;
  if (typeof ts === "string") return Math.floor(new Date(ts).getTime() / 1000);
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return ts.seconds;
    if (typeof ts._seconds === "number") return ts._seconds;
    if (typeof ts.toDate === "function") return Math.floor(ts.toDate().getTime() / 1000);
  }
  return 0;
}

export default function AssignmentProgress({ assignment, classInfo, students = [] }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAttempts, setOpenAttempts] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    const qRef = query(collection(db, "test_histories"), where("assignmentId", "==", assignment.id));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSubs(arr);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [assignment.id]);

  const effectiveStudents = useMemo(() => {
    if (!assignment.assignedTo || !Array.isArray(assignment.assignedTo) || assignment.assignedTo.length === 0) {
      return students;
    }
    const assignedSet = new Set(assignment.assignedTo);
    return students.filter((s) => assignedSet.has(s.id));
  }, [assignment.assignedTo, students]);

  const perStudent = useMemo(() => {
    const map = new Map();
    subs.forEach((sub) => {
      const key = sub.userId;
      const prev = map.get(key) || { count: 0, bestPercent: null, lastAt: null };
      const pct = getPercent(sub);
      prev.count += 1;
      if (typeof pct === "number") {
        prev.bestPercent = prev.bestPercent == null ? pct : Math.max(prev.bestPercent, pct);
      }
      const currentSeconds = tsSeconds(sub.submittedAt || sub.updatedAt);
      const previousSeconds = tsSeconds(prev.lastAt);
      prev.lastAt = currentSeconds >= previousSeconds ? sub.submittedAt || sub.updatedAt : prev.lastAt;
      map.set(key, prev);
    });
    return map;
  }, [subs]);

  const doneCount = effectiveStudents.filter((stu) => (perStudent.get(stu.id)?.count || 0) > 0).length;
  const total = effectiveStudents.length;
  const percent = total ? Math.round((doneCount / total) * 100) : 0;
  const percents = subs.map(getPercent).filter((x) => typeof x === "number");
  const avg = percents.length ? Math.round(percents.reduce((a, b) => a + b, 0) / percents.length) : 0;
  const best = percents.length ? Math.max(...percents) : 0;
  const unpublished = !assignment?.published;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h4 className="font-bold text-slate-900">Tiến độ học viên</h4>
          <p className="text-sm text-slate-500">
            Hạn: {assignment.deadline ? dayjs(assignment.deadline).format("DD/MM/YYYY") : "—"} {assignment.allowLate ? "(cho nộp trễ)" : ""}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl border bg-white px-3 py-2">
            <div className="font-bold text-blue-700">{doneCount}/{total}</div>
            <div className="text-[11px] text-slate-500">Đã làm</div>
          </div>
          <div className="rounded-xl border bg-white px-3 py-2">
            <div className="font-bold text-emerald-600">{avg}%</div>
            <div className="text-[11px] text-slate-500">Điểm TB</div>
          </div>
          <div className="rounded-xl border bg-white px-3 py-2">
            <div className="font-bold text-amber-600">{best}%</div>
            <div className="text-[11px] text-slate-500">Cao nhất</div>
          </div>
        </div>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${percent}%` }} />
      </div>

      {unpublished && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <FiAlertTriangle className="mt-0.5 shrink-0" />
          Assignment đang ở trạng thái <b>Draft</b>. Học viên chưa thể mở bài cho tới khi bạn công khai.
        </div>
      )}

      {loading ? (
        <div className="mt-4 rounded-xl border border-dashed bg-white p-4 text-sm text-slate-500">Đang tải bài nộp...</div>
      ) : !effectiveStudents.length ? (
        <div className="mt-4 rounded-xl border border-dashed bg-white p-4 text-sm text-slate-500">
          Assignment này chưa chọn học viên nào.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {effectiveStudents.map((stu) => {
            const stat = perStudent.get(stu.id);
            const lastSeconds = tsSeconds(stat?.lastAt);
            const last = lastSeconds ? dayjs(lastSeconds * 1000).format("DD/MM HH:mm") : "Chưa nộp";
            const testUrl = routePaths.helpers.testPage(assignment.testId, assignment.id, classInfo.id);
            const submitted = (stat?.count || 0) > 0;

            return (
              <div key={stu.id} className="rounded-xl border bg-white p-3 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          submitted
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-100 text-slate-600"
                        }`}
                      >
                        {submitted ? "Đã có bài nộp" : "Chưa nộp"}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
                        <FiUsers /> {stat?.count || 0} lần
                      </span>
                    </div>

                    <div className="mt-2 font-semibold text-slate-900">{studentName(stu)}</div>
                    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <FiTrendingUp /> Best: {typeof stat?.bestPercent === "number" ? `${stat.bestPercent}%` : "—"}
                      </span>
                      <span>Gần nhất: {last}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 lg:w-56 shrink-0">
                    {unpublished ? (
                      <span className="inline-flex flex-1 items-center justify-center rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-400">
                        Chưa công khai
                      </span>
                    ) : (
                      <a
                        href={testUrl}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FiExternalLink /> Mở bài
                      </a>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStudent(stu);
                        setOpenAttempts(true);
                      }}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <FiRotateCcw /> Lần nộp
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AttemptsDrawer
        open={openAttempts}
        onClose={() => setOpenAttempts(false)}
        student={selectedStudent}
        assignment={assignment}
        classInfo={classInfo}
      />
    </div>
  );
}
